import React from 'react';
import { FieldValues, Path } from 'react-hook-form';
import FormInput from './FormInput';
import ControlledRadioGroup from './FormRadioGroup';
import { ControlledCheckboxGroup } from './FormCheckboxGroup';
import FormSelect from './FormSelect';
import FormMultiSelect from './FormMultiSelect';
import { SelectOption } from '../types';
import { FormPhoneNumberField } from './index';
import { Question } from '../../questionnaire/hooks/useQuestionnaire';
import { DynamicQuestionnaireDependencie } from '../../dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import CheckboxGroupWithNoneOptionField from '../../../app/manage-test-kits/components/MicrobiomeQuestionnaire/CustomFields/CheckboxGroupWithNoneOptionField';
import { NestedDependentsRenderer } from './NestedQuestionnaireFields';
import FormDatePicker from './FormDatePicker';
import { UI_CONTROL } from '../../dynamicQuestionnaire/interfaces/uiControl.interface';
import FormYearPicker from './FormYearPicker';
import { ArrayQuestionnaireField } from '../../questionnaire/components/ArrayQuestionnaireField';
import FormTimePicker from './FormTimePicker';
import { FormDropzoneField } from './FormFileDropzone';
import { ContentType } from 'graphql/generated/hasura';
import { createAcceptPolicy } from 'utilities/fileHelpers';

export const findOtherFieldDependency = (
  question: Question,
  dependencies: DynamicQuestionnaireDependencie[],
): { dependentFieldId: string; triggerValues: string[] }[] => {
  if (!dependencies) return [];

  const result: { dependentFieldId: string; triggerValues: string[] }[] = [];

  for (const dep of dependencies) {
    const ifProps = dep.if?.properties;
    if (!ifProps) continue;

    const triggerId = Object.keys(ifProps)[0];
    const constValue = ifProps[triggerId]?.const as string;
    const dependentFieldId = dep.then?.required?.[0] ?? dep.then?.enabled?.[0];

    if (
      triggerId === String(question.id) &&
      dependentFieldId &&
      dependentFieldId !== triggerId
    ) {
      let existing = result.find(
        (res) => res.dependentFieldId === dependentFieldId,
      );
      if (!existing) {
        existing = { dependentFieldId, triggerValues: [] };
        result.push(existing);
      }
      existing.triggerValues.push(constValue);
    }
  }

  return result;
};

export type UIControl =
  | 'TEXT_FIELD'
  | 'NUMBER_FIELD'
  | 'RADIO_BUTTON'
  | 'LOOKUP_FIELD'
  | 'LOOKUP_WITH_MULTISELECT'
  | 'TEXT_AREA'
  | 'RADIO'
  | string;

export type QuestionnaireFieldProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string;
  ui: UIControl;
  answers?: string[] | null;
  placeholder?: string | null;
  subtitle?: string | null;
  filesName?: string | null;
  types?: ContentType[];
  required?: boolean;
  disabled?: boolean;
};

export const QuestionnaireField = <TFormValues extends FieldValues>({
  ui,
  name = '' as Path<TFormValues>,
  label,
  answers,
  required,
  subtitle,
  types = [],
  filesName,
  placeholder,
  disabled,
}: QuestionnaireFieldProps<TFormValues>) => {
  const ACCEPTED_FILE_TYPES = createAcceptPolicy(types);
  switch (ui) {
    case 'TEXT_FIELD':
      return (
        <FormInput<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          type="text"
          describe={subtitle}
          disabled={disabled}
        />
      );

    case 'TEXT':
      return (
        <FormInput<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          type="text"
          describe={subtitle}
          disabled={disabled}
        />
      );

    case 'NUMBER_FIELD':
      return (
        <FormInput<TFormValues>
          name={name}
          label={label}
          isRequired={required}
          placeholder={placeholder ?? ''}
          type="number"
          disabled={disabled}
        />
      );

    case 'IMAGE':
      return (
        <FormDropzoneField<TFormValues>
          name={name}
          label={label}
          accept={ACCEPTED_FILE_TYPES}
          supportText={`Supported file formats: ${types.join(', ')}`}
          filesName={filesName as Path<TFormValues>}
          labelDropzone="Drop file here to upload"
          placeholder={placeholder}
          isRequired={required}
          disabled={disabled}
        >
          <div className="flex flex-col gap-1.5 text-med-gray-3 my-2.5 text-sm font-medium">
            <p className="text-neutral-800 font-semibold -mb-1.5">
              Why this matters:
            </p>
            <p>
              Stool appearance provides important clues about digestive health.
              Clinicians use the Bristol Stool Form Scale to classify stool
              types and better understand gut function.
            </p>
          </div>
        </FormDropzoneField>
      );

    case 'RADIO_BUTTON':
    case 'RADIO': {
      const options =
        (answers ?? []).map((a) => ({ label: a, value: a })) ?? [];
      return (
        <ControlledRadioGroup<TFormValues>
          name={name}
          label={label}
          options={options}
          isRequired={required}
          disabled={disabled}
        />
      );
    }
    case 'CHECKBOX_GROUP': {
      const options =
        (answers ?? []).map((a) => ({ label: a, value: a })) ?? [];
      return (
        <ControlledCheckboxGroup<TFormValues>
          name={name}
          label={label}
          options={options}
          isRequired={required}
          disabled={disabled}
        />
      );
    }

    // case 'CHECKBOX_GROUP':
    //   return (
    //     <CheckboxGroupWithNoneOptionField
    //       key={name}
    //       question={question}
    //       dependencies={dependencies}
    //       isRequired={!!required}
    //       isDisabled={!!disabled}
    //       questionsById={questionsById}
    //     />
    //   )

    case 'PHONE': {
      return (
        <FormPhoneNumberField
          defaultValue={null}
          name={name}
          label={label}
          isRequired={required}
        />
      );
    }

    case 'EMAIL': {
      return (
        <FormInput<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          type="email"
          disabled={disabled}
        />
      );
    }

    case 'LOOKUP_FIELD':
    case 'DROPDOWN_50_PERCENT':
    case 'DROPDOWN_35_PERCENT': {
      const options: SelectOption[] =
        (answers ?? []).map((a) => ({ label: a, value: a })) ?? [];
      return (
        <FormSelect<TFormValues>
          name={name}
          label={label}
          options={options}
          placeholder={placeholder ?? ''}
          isRequired={required}
          describe={subtitle}
          disabled={disabled}
        />
      );
    }

    case 'LOOKUP_WITH_MULTISELECT': {
      const options: SelectOption[] =
        (answers ?? []).map((a) => ({ label: a, value: a })) ?? [];
      return (
        <FormMultiSelect<TFormValues>
          name={name}
          label={label}
          options={options}
          placeholder={placeholder ?? ''}
          isRequired={required}
          disabled={disabled}
        />
      );
    }

    case 'DATE': {
      return (
        <FormDatePicker<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          disabled={disabled}
        />
      );
    }

    case 'YEAR_PICKER': {
      return (
        <FormYearPicker<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          disabled={disabled}
          describe={subtitle}
        />
      );
    }

    case 'TIME': {
      return (
        <FormTimePicker<TFormValues>
          name={name}
          label={label}
          isRequired={required}
          disabled={disabled}
          describe={subtitle}
        />
      );
    }

    default:
      return (
        <FormInput<TFormValues>
          name={name}
          label={label}
          placeholder={placeholder ?? ''}
          isRequired={required}
          type="text"
          disabled={disabled}
        />
      );
  }
};

export type CustomFieldProps = {
  question: Question;
  dependencies: DynamicQuestionnaireDependencie[];
  questionsById: Record<string, Question>;
  isRequired: boolean;
  isDisabled: boolean;
  dynamicDeps?: {
    requiredFields: string[];
    disabledFields: string[];
  };
};

interface SmartQuestionRendererProps {
  questionList: Question[];
  types?: ContentType[];
  dependencies: DynamicQuestionnaireDependencie[];
  questionsById: Record<string, Question>;
  dynamicDeps: {
    requiredFields: string[];
    disabledFields: string[];
  };
  hiddenFieldsIds?: string[];
  customFields?: Partial<Record<string, React.ComponentType<CustomFieldProps>>>;
}

export const SmartQuestionRenderer = <TFormValues extends FieldValues>({
  questionList,
  types = [],
  hiddenFieldsIds = [],
  dependencies,
  questionsById,
  dynamicDeps,
  customFields,
}: SmartQuestionRendererProps) => {
  const renderedIds = new Set<string>();
  return (
    <>
      {questionList.map((question, index) => {
        const idStr = String(question.id);

        const compositeInfo = findOtherFieldDependency(
          question,
          dependencies ?? [],
        );

        if (renderedIds.has(idStr)) {
          return null;
        }

        const isRequired = dynamicDeps.requiredFields.includes(idStr);
        const isDisabled = dynamicDeps.disabledFields.includes(idStr);
        const CustomFieldCmp = customFields?.[idStr];

        const nestedFieldQuestions = compositeInfo
          .map(({ dependentFieldId }) => questionsById[dependentFieldId])
          .filter(Boolean);

        nestedFieldQuestions.forEach((nested) => {
          renderedIds.add(String(nested.id));
        });

        const renderField = () => {
          if (CustomFieldCmp) {
            renderedIds.add(idStr);

            return (
              <CustomFieldCmp
                key={idStr}
                question={question}
                dependencies={dependencies}
                dynamicDeps={dynamicDeps}
                isRequired={isRequired}
                isDisabled={isDisabled}
                questionsById={questionsById}
              />
            );
          }

          if (['ARRAY_FIELD'].includes(question.ui_control)) {
            renderedIds.add(idStr);

            return (
              <div className="p-5 bg-base-content rounded-10 h-full">
                <ArrayQuestionnaireField<TFormValues>
                  question={question}
                  dependencies={dependencies}
                  dynamicDeps={dynamicDeps}
                  questionsById={questionsById}
                  customFields={customFields}
                />
              </div>
            );
          }

          if (['TWENTY_FIVE_PRECENT'].includes(question.ui_control)) {
            renderedIds.add(idStr);

            return (
              <div className="p-5 bg-base-content rounded-10 h-full">
                <QuestionnaireField<TFormValues>
                  key={idStr}
                  types={types}
                  name={idStr as Path<TFormValues>}
                  label={question.text as string}
                  ui={UI_CONTROL.RADIO_BUTTON as UIControl}
                  answers={(question.answers ?? []).map(
                    (item, index) => `${index} - ${item}`,
                  )}
                  placeholder={question.helper_text}
                  required={isRequired}
                  disabled={isDisabled}
                  subtitle={question.subtitle}
                />
              </div>
            );
          }

          if (
            ['CHECKBOX_GROUP_ADVANCED', 'CHECKBOX', 'CHECKBOX_GROUP'].includes(
              question.ui_control,
            )
          ) {
            renderedIds.add(idStr);

            return (
              <CheckboxGroupWithNoneOptionField
                key={idStr}
                question={question}
                dependencies={dependencies}
                isRequired={isRequired}
                isDisabled={isDisabled}
                questionsById={questionsById}
              />
            );
          }

          if (question.ui_control === 'PHONE') {
            const nextQuestion = questionList[index + 1];
            if (
              nextQuestion?.ui_control === 'SMALL_TEXT' &&
              nextQuestion.text === 'Extension'
            ) {
              const extIdStr = String(nextQuestion.id);
              renderedIds.add(idStr);
              renderedIds.add(extIdStr);

              return (
                <FormPhoneNumberField<TFormValues>
                  key={idStr}
                  defaultValue={null}
                  name={idStr as Path<TFormValues>}
                  label={question.text as string}
                  withoutExt={!extIdStr}
                />
              );
            }
          }

          renderedIds.add(idStr);

          return (
            <QuestionnaireField<TFormValues>
              key={idStr}
              types={types}
              name={idStr as Path<TFormValues>}
              filesName={question.response_property}
              label={question.text as string}
              ui={question.ui_control as UIControl}
              answers={question.answers ?? []}
              placeholder={question.helper_text}
              required={isRequired}
              disabled={isDisabled}
              subtitle={question.subtitle}
            />
          );
        };

        return (
          <div>
            {renderField()}

            <NestedDependentsRenderer
              parentQuestion={question}
              dependencies={dependencies}
              questionsById={questionsById}
              customFields={customFields}
              hiddenFieldsIds={hiddenFieldsIds}
              dynamicDeps={dynamicDeps}
            />
          </div>
        );
      })}
    </>
  );
};
